/**
 * Состояние роута как поток: согласованный снапшот после каждой навигации (SEO-8 §7).
 *
 * **Зачем вообще нужен снапшот, а не `combineLatest([paramMap, queryParams])`.**
 * Angular пушит `params` и `queryParams` двумя эмиссиями в РАЗНЫХ тактах (замерено ≈1 мс),
 * поэтому `combineLatest` успевает выдать «полустарую» пару: новый query с ключом из
 * ПРЕДЫДУЩЕЙ навигации. В каталоге это стоило лишнего ES-запроса с чужим `catalog_id` на
 * каждом переходе между категориями. `debounceTime(0)` не спасает — он схлопывает только
 * синхронный глитч. Снапшот, прочитанный после `NavigationEnd`, согласован по определению.
 *
 * **Зачем это в общем модуле.** Идентификатор категории уехал из query в ПУТЬ, а
 * компоненты лейаута (`filter-component`, `modal-catalog`) path-параметра каталожного
 * роута не видят: их `ActivatedRoute` — корневой. Скелет «`NavigationEnd` → прочитать
 * снапшот → отбросить повтор» нужен трём потребителям, и первая версия расползлась по
 * ним копиями, которые сразу же разъехались: дедупликация появилась только в одном.
 *
 * **Дедупликация обязательна и обязана учитывать ВСЁ, что читает потребитель.**
 * `NavigationEnd` срабатывает на любой навигации приложения, поэтому без неё переход
 * `/cart` → `/favorites` дергал бы подписчиков впустую. Но и ключ по одному полю опасен:
 * дедупликация «только по категории» скрыла от фильтр-панели смену query, и фасеты
 * перестали обновляться при применении фильтра. Отсюда явный компаратор в API.
 *
 * Живёт не в `url-builder.ts` осознанно: тот модуль намеренно не знает про Angular
 * (его node-env спека не поднимает `@angular/router` и не должна).
 */
import {HttpParams} from '@angular/common/http';
import {NavigationEnd, Params, Router} from '@angular/router';
import {Observable} from 'rxjs';
import {distinctUntilChanged, filter, map, startWith} from 'rxjs/operators';

/**
 * @param router роутер приложения (у компонентов он есть из `BaseComponent`)
 * @param read   что прочитать из состояния роута; зовётся ПОСЛЕ завершения навигации
 * @param isSame когда две прочитанные величины считаются одной и той же — обязан
 *   сравнивать всё, что `read` вернул, иначе потребитель молча пропустит изменение
 *
 * @returns снапшоты: сразу при подписке (`startWith`) и далее на каждой РЕАЛЬНОЙ смене
 */
export function navigationSnapshot$<T>(
  router: Router,
  read: () => T,
  isSame: (a: T, b: T) => boolean,
): Observable<T> {
  return router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    startWith(null),
    map(read),
    distinctUntilChanged(isSame),
  );
}

/**
 * Стабильный ключ набора query-параметров: порядок ключей не гарантирован, а сравнивать
 * надо ЗНАЧЕНИЯ — иначе переупорядоченный query читался бы как изменение.
 *
 * Принимает `snapshot.queryParams` (обычный объект). Потребителю, которому нужен
 * `ParamMap` (например для `paramsToHttp`), он доступен из ТОГО ЖЕ снапшота — то есть
 * обе формы согласованы между собой по построению.
 */
export function queryKey(query: Params): string {
  return Object.keys(query).sort(byCodeUnit).map(key => `${key}=${String(query[key])}`).join('&');
}

/**
 * Порядок ключей в кэш-ключе — по кодовым единицам, ЯВНЫМ компаратором.
 *
 * Намеренно НЕ `localeCompare`, хотя линтер предлагает именно его: здесь сортировка не
 * для показа человеку, а для построения ключа дедупликации. `localeCompare` зависит от
 * локали и версии ICU (один и тот же набор параметров дал бы разные ключи в разных
 * окружениях) и, главное, может признать РАЗНЫЕ строки равными — два несовпадающих
 * набора параметров схлопнулись бы в один ключ, и обновление потерялось бы. Это ровно
 * тот класс дефектов, против которого написаны `queryKey`/`paramsKey`.
 *
 * Поведение совпадает с `Array.prototype.sort()` без аргумента — компаратор здесь ради
 * явности намерения, а не ради смены порядка.
 */
function byCodeUnit(a: string, b: string): number {
  if (a < b) {
    return -1;
  }
  return a > b ? 1 : 0;
}

/*
 * Готового `catalogId$` здесь НЕТ намеренно: оба потребителя в лейауте читают из снапшота
 * не только категорию (фильтр-панель — ещё и query, дерево каталога — limit/region), и
 * обёртка «только категория» уже один раз обошлась дороже, чем стоила: с ней в ключ
 * дедупликации не попадало остальное, и потребители пропускали изменения. Категорию из
 * пути отдаёт `catalogIdFromPath` (`url-builder.ts`); `search-autocomplete` зовёт её
 * синхронно, без подписки, и это правильно — значение нужно в момент поиска.
 */

/**
 * Стабильный ключ набора параметров ЗАПРОСА (не URL).
 *
 * Живёт рядом с `queryKey` по той же причине: оба отвечают на вопрос «одно и то же это
 * состояние или уже другое», и разъехавшиеся ответы дают либо лишний запрос, либо
 * пропущенное обновление.
 *
 * `HttpParams.toString()` для этого не годится — он хранит порядок вставки. На холодном
 * входе в категорию листинг собирал ОДИН И ТОТ ЖЕ набор дважды в разном порядке
 * (`catalog_id&region` и `region&catalog_id`), дедупликация по строке их не склеивала, и
 * уходил лишний ES-запрос. Замерено в браузере.
 */
export function paramsKey(params: HttpParams): string {
  return params.keys().sort(byCodeUnit).map(key => `${key}=${(params.getAll(key) ?? []).join(',')}`).join('&');
}
